import { useEffect, useRef, useState, type ChangeEvent } from "react";
import {
  Loader2,
  Palette,
  RefreshCw,
  Save,
  Settings,
  Sliders,
  Upload,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  DEFAULT_BRANDING,
  DEFAULT_GENERAL_SETTINGS,
  DEFAULT_REPORT_TEMPLATE,
  useConfig,
  useSaveConfig,
  type BrandingSettings,
  type GeneralSettings,
  type ReportTemplate,
} from "../hooks/useConfig";

type Tab = "general" | "report" | "branding";

const BORDER = "hsl(210,30%,85%)";
const CARD_BG = "white";
const MUTED = "hsl(215,16%,47%)";

const TABS: { id: Tab; label: string }[] = [
  { id: "general", label: "General Settings" },
  { id: "report", label: "Report Template" },
  { id: "branding", label: "Branding" },
];

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div
        className="border-b pb-2 text-[13px] font-semibold uppercase tracking-wide text-foreground"
        style={{ borderColor: BORDER }}
      >
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({
  label,
  children,
  hint,
}: {
  label?: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
      {children}
      {hint && <div className="text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function ColorInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-12 cursor-pointer rounded-md border"
        style={{ borderColor: BORDER, padding: 2 }}
      />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 font-mono text-xs"
      />
    </div>
  );
}

function RadioGroup<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-2">
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex-1 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
              active
                ? "bg-[hsl(210,55%,92%)] text-[hsl(210,100%,28%)]"
                : "bg-white text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
            }`}
            style={{ borderColor: active ? "hsl(210,68%,55%)" : BORDER }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 rounded-full transition-colors ${
          checked ? "bg-[hsl(210,68%,55%)]" : "bg-[hsl(210,16%,85%)]"
        }`}
      >
        <span
          className="absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform"
          style={{ left: checked ? "calc(100% - 18px)" : 2 }}
        />
      </button>
    </label>
  );
}

function LogoUploader({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  function handleFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") onChange(reader.result);
    };
    reader.readAsDataURL(file);
    if (inputRef.current) inputRef.current.value = "";
  }

  return (
    <div className="space-y-2">
      {value ? (
        <div
          className="flex items-center gap-3 rounded-md border p-2"
          style={{ borderColor: BORDER }}
        >
          <img
            src={value}
            alt="Logo"
            className="h-12 w-auto max-w-[140px] object-contain"
          />
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange(null)}
          >
            <X className="h-3.5 w-3.5" strokeWidth={1.5} />
            Remove
          </Button>
        </div>
      ) : (
        <Button
          variant="outline"
          type="button"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4" strokeWidth={1.5} />
          Upload Logo
        </Button>
      )}
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        accept="image/*"
        onChange={handleFile}
      />
    </div>
  );
}

function ReportPreview({ template }: { template: ReportTemplate }) {
  const headerAlign =
    template.header.titleAlign === "center"
      ? "center"
      : template.header.titleAlign === "right"
      ? "right"
      : "left";
  const logoAlignClass =
    template.header.logoPosition === "center"
      ? "justify-center"
      : template.header.logoPosition === "right"
      ? "justify-end"
      : "justify-start";
  const logoHeight =
    template.header.logoSize === "small"
      ? 28
      : template.header.logoSize === "large"
      ? 52
      : 40;

  const footerFont = template.footer.fontSize === "normal" ? 10 : 9;
  const footerColor = template.footer.textColor || "#888888";

  function footerContent(
    type: string,
    customText: string,
    fallback: string,
  ): string {
    switch (type) {
      case "":
      case "empty":
        return "";
      case "page_numbers":
        return "Page 1 of 1";
      case "date":
        return `Generated: ${new Date().toLocaleDateString("en-GB")}`;
      case "report_name":
        return template.header.useReportNameAuto
          ? "Tasks"
          : template.header.reportTitle || "Report";
      case "company_name":
        return template.header.companyName || "PhazeX";
      case "confidential":
        return "Confidential — Internal Use Only";
      case "prepared_by":
        return "Prepared by PhazeX Hub";
      case "custom":
        return customText || fallback;
      default:
        return type;
    }
  }

  const sampleRows = [
    {
      project: "Nebula Platform",
      status: "In Progress",
      due: "30 Apr 2026",
      progress: "65%",
    },
    {
      project: "Orion UI Refresh",
      status: "Open",
      due: "12 May 2026",
      progress: "20%",
    },
    {
      project: "Atlas Integration",
      status: "Completed",
      due: "02 Apr 2026",
      progress: "100%",
    },
    {
      project: "Helios Mobile",
      status: "In Review",
      due: "18 May 2026",
      progress: "80%",
    },
  ];

  return (
    <div className="space-y-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        Preview
      </div>
      <div
        className="aspect-[1/1.414] w-full overflow-hidden rounded-md border shadow-lg"
        style={{
          borderColor: BORDER,
          background: template.header.backgroundColor,
          fontFamily: template.typography.fontFamily,
        }}
      >
        <div className="flex h-full flex-col p-5 text-[10px]">
          {/* Header */}
          <div
            className={`mb-2 flex flex-wrap items-center gap-3 ${logoAlignClass}`}
          >
            {template.header.logo && (
              <img
                src={template.header.logo}
                alt="Logo"
                style={{ height: logoHeight }}
                className="object-contain"
              />
            )}
          </div>
          <div
            className="flex items-start justify-between gap-3"
            style={{ textAlign: headerAlign as "left" | "center" | "right" }}
          >
            <div className="flex-1">
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: template.colors.primary,
                  textAlign: headerAlign as "left" | "center" | "right",
                }}
              >
                {template.header.useReportNameAuto
                  ? "Tasks"
                  : template.header.reportTitle || "Report"}
              </div>
              {template.header.subtitle && (
                <div
                  className="italic"
                  style={{ color: "#666", fontSize: 10 }}
                >
                  {template.header.subtitle}
                </div>
              )}
            </div>
            {template.header.showCompanyInfo && (
              <div className="text-right text-[9px] text-muted-foreground">
                <div className="font-semibold text-foreground">
                  {template.header.companyName}
                </div>
                {template.header.addressLine1 && (
                  <div>{template.header.addressLine1}</div>
                )}
                {template.header.addressLine2 && (
                  <div>{template.header.addressLine2}</div>
                )}
              </div>
            )}
          </div>
          {template.header.showDivider && (
            <div
              className="my-2"
              style={{
                height: 1,
                background: template.header.dividerColor,
              }}
            />
          )}
          <div className="mb-1 text-[9px] text-muted-foreground">
            Generated: {new Date().toLocaleDateString("en-GB")}
          </div>

          {/* Table */}
          <div
            className="overflow-hidden rounded-sm border"
            style={{ borderColor: "#E5E7EB" }}
          >
            <div
              className="grid grid-cols-4 text-[9px] font-semibold uppercase"
              style={{
                background: template.colors.headerBg,
                color: template.colors.headerText,
                padding: "6px 8px",
              }}
            >
              <div>Project Name</div>
              <div>Status</div>
              <div>Due Date</div>
              <div>Progress</div>
            </div>
            {sampleRows.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-4 border-t text-[9px]"
                style={{
                  borderColor: "#F3F4F6",
                  padding: "5px 8px",
                  background: i % 2 === 1 ? template.colors.altRow : "white",
                }}
              >
                <div>{row.project}</div>
                <div>{row.status}</div>
                <div>{row.due}</div>
                <div>{row.progress}</div>
              </div>
            ))}
          </div>

          <div className="flex-1" />

          {/* Footer */}
          {template.footer.showDivider && (
            <div
              className="mt-2"
              style={{ height: 1, background: "#D1D5DB" }}
            />
          )}
          <div
            className="mt-1 grid grid-cols-3 gap-2"
            style={{ color: footerColor, fontSize: footerFont }}
          >
            <div>
              {footerContent(template.footer.left, template.footer.leftText, "")}
            </div>
            <div className="text-center">
              {footerContent(
                template.footer.center,
                template.footer.centerText,
                "",
              )}
            </div>
            <div className="text-right">
              {footerContent(
                template.footer.right,
                template.footer.rightText,
                "",
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {template.page.size} {template.page.orientation === "portrait" ? "Portrait" : "Landscape"}
      </div>
    </div>
  );
}

const FOOTER_OPTIONS = [
  { value: "", label: "Empty" },
  { value: "page_numbers", label: "Page Numbers" },
  { value: "date", label: "Date Generated" },
  { value: "report_name", label: "Report Name" },
  { value: "company_name", label: "Company Name" },
  { value: "custom", label: "Custom Text" },
  { value: "confidential", label: "Confidentiality Notice" },
  { value: "prepared_by", label: "Prepared By" },
];

function GeneralSettingsTab({
  value,
  onChange,
  onSave,
  saving,
  saved,
}: {
  value: GeneralSettings;
  onChange: (v: GeneralSettings) => void;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
}) {
  function set<K extends keyof GeneralSettings>(k: K, v: GeneralSettings[K]) {
    onChange({ ...value, [k]: v });
  }
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">General Settings</h2>
        <p className="text-xs text-muted-foreground">
          Platform-wide defaults used across the app.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Platform Name">
          <Input
            value={value.platformName}
            onChange={(e) => set("platformName", e.target.value)}
          />
        </Field>
        <Field label="Platform URL">
          <Input
            value={value.platformUrl}
            onChange={(e) => set("platformUrl", e.target.value)}
          />
        </Field>
        <Field label="Default Currency">
          <Select
            value={value.defaultCurrency}
            onChange={(e) => set("defaultCurrency", e.target.value)}
          >
            <option value="AED">AED — UAE Dirham</option>
            <option value="USD">USD — US Dollar</option>
            <option value="EUR">EUR — Euro</option>
            <option value="GBP">GBP — British Pound</option>
            <option value="INR">INR — Indian Rupee</option>
            <option value="SAR">SAR — Saudi Riyal</option>
          </Select>
        </Field>
        <Field label="Default Date Format">
          <Select
            value={value.defaultDateFormat}
            onChange={(e) => set("defaultDateFormat", e.target.value)}
          >
            <option value="DD/MM/YYYY">DD/MM/YYYY</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD</option>
          </Select>
        </Field>
        <Field label="Default Timezone">
          <Select
            value={value.defaultTimezone}
            onChange={(e) => set("defaultTimezone", e.target.value)}
          >
            <option value="Asia/Dubai">Asia/Dubai</option>
            <option value="Asia/Kolkata">Asia/Kolkata</option>
            <option value="Europe/London">Europe/London</option>
            <option value="America/New_York">America/New_York</option>
            <option value="Asia/Riyadh">Asia/Riyadh</option>
          </Select>
        </Field>
        <Field label="Default Language">
          <Select
            value={value.defaultLanguage}
            onChange={(e) => set("defaultLanguage", e.target.value)}
          >
            <option value="English">English</option>
            <option value="Arabic">Arabic</option>
            <option value="Hindi">Hindi</option>
            <option value="French">French</option>
            <option value="German">German</option>
          </Select>
        </Field>
      </div>
      <div className="flex items-center gap-3 pt-4">
        <Button onClick={onSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Save className="h-4 w-4" strokeWidth={1.5} />
          )}
          Save Settings
        </Button>
        {saved && (
          <span className="text-xs text-green-700">Saved successfully</span>
        )}
      </div>
    </div>
  );
}

function ReportTemplateTab({
  template,
  onChange,
  onSave,
  onReset,
  saving,
  saved,
}: {
  template: ReportTemplate;
  onChange: (v: ReportTemplate) => void;
  onSave: () => void;
  onReset: () => void;
  saving: boolean;
  saved: boolean;
}) {
  function updateHeader<K extends keyof ReportTemplate["header"]>(
    k: K,
    v: ReportTemplate["header"][K],
  ) {
    onChange({ ...template, header: { ...template.header, [k]: v } });
  }
  function updateFooter<K extends keyof ReportTemplate["footer"]>(
    k: K,
    v: ReportTemplate["footer"][K],
  ) {
    onChange({ ...template, footer: { ...template.footer, [k]: v } });
  }
  function updateColors<K extends keyof ReportTemplate["colors"]>(
    k: K,
    v: ReportTemplate["colors"][K],
  ) {
    onChange({ ...template, colors: { ...template.colors, [k]: v } });
  }
  function updatePage<K extends keyof ReportTemplate["page"]>(
    k: K,
    v: ReportTemplate["page"][K],
  ) {
    onChange({ ...template, page: { ...template.page, [k]: v } });
  }
  function updateTypo<K extends keyof ReportTemplate["typography"]>(
    k: K,
    v: ReportTemplate["typography"][K],
  ) {
    onChange({
      ...template,
      typography: { ...template.typography, [k]: v },
    });
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Report Template</h2>
        <p className="text-xs text-muted-foreground">
          Configure how exported reports look. Preview updates live.
        </p>
      </div>

      <div className="grid grid-cols-[55fr_45fr] gap-6">
        {/* Left — Configuration */}
        <div className="space-y-6">
          <Section title="Page Header">
            <Field label="Logo">
              <LogoUploader
                value={template.header.logo}
                onChange={(v) => updateHeader("logo", v)}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Logo Position">
                <RadioGroup
                  value={template.header.logoPosition}
                  options={[
                    { value: "left", label: "Left" },
                    { value: "center", label: "Center" },
                    { value: "right", label: "Right" },
                  ]}
                  onChange={(v) => updateHeader("logoPosition", v)}
                />
              </Field>
              <Field label="Logo Size">
                <RadioGroup
                  value={template.header.logoSize}
                  options={[
                    { value: "small", label: "Small" },
                    { value: "medium", label: "Medium" },
                    { value: "large", label: "Large" },
                  ]}
                  onChange={(v) => updateHeader("logoSize", v)}
                />
              </Field>
            </div>

            <Field label="Report Title">
              <Input
                value={
                  template.header.useReportNameAuto
                    ? "{Report Name}"
                    : template.header.reportTitle
                }
                disabled={template.header.useReportNameAuto}
                onChange={(e) => updateHeader("reportTitle", e.target.value)}
              />
              <div className="mt-1">
                <Toggle
                  checked={template.header.useReportNameAuto}
                  onChange={(v) => updateHeader("useReportNameAuto", v)}
                  label="Use report name automatically"
                />
              </div>
            </Field>

            <Field
              label="Subtitle"
              hint="e.g. Confidential, Internal Use Only, department"
            >
              <Input
                value={template.header.subtitle}
                onChange={(e) => updateHeader("subtitle", e.target.value)}
                placeholder="Optional subtitle"
              />
            </Field>

            <Field label="Title Alignment">
              <RadioGroup
                value={template.header.titleAlign}
                options={[
                  { value: "left", label: "Left" },
                  { value: "center", label: "Center" },
                  { value: "right", label: "Right" },
                ]}
                onChange={(v) => updateHeader("titleAlign", v)}
              />
            </Field>

            <div
              className="rounded-md border p-3"
              style={{ borderColor: BORDER }}
            >
              <Toggle
                checked={template.header.showCompanyInfo}
                onChange={(v) => updateHeader("showCompanyInfo", v)}
                label="Show company info on header"
              />
              {template.header.showCompanyInfo && (
                <div className="mt-3 space-y-2">
                  <Field label="Company Name">
                    <Input
                      value={template.header.companyName}
                      onChange={(e) =>
                        updateHeader("companyName", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="Address Line 1">
                    <Input
                      value={template.header.addressLine1}
                      onChange={(e) =>
                        updateHeader("addressLine1", e.target.value)
                      }
                    />
                  </Field>
                  <Field label="Address Line 2">
                    <Input
                      value={template.header.addressLine2}
                      onChange={(e) =>
                        updateHeader("addressLine2", e.target.value)
                      }
                    />
                  </Field>
                </div>
              )}
            </div>

            <div
              className="rounded-md border p-3"
              style={{ borderColor: BORDER }}
            >
              <Toggle
                checked={template.header.showDivider}
                onChange={(v) => updateHeader("showDivider", v)}
                label="Show header divider line"
              />
              {template.header.showDivider && (
                <div className="mt-3">
                  <Field label="Divider Color">
                    <ColorInput
                      value={template.header.dividerColor}
                      onChange={(v) => updateHeader("dividerColor", v)}
                    />
                  </Field>
                </div>
              )}
            </div>

            <Field label="Header Background">
              <ColorInput
                value={template.header.backgroundColor}
                onChange={(v) => updateHeader("backgroundColor", v)}
              />
            </Field>
          </Section>

          <Section title="Page Footer">
            <div
              className="rounded-md border p-3"
              style={{ borderColor: BORDER }}
            >
              <Toggle
                checked={template.footer.showDivider}
                onChange={(v) => updateFooter("showDivider", v)}
                label="Show footer divider"
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Field label="Left">
                <Select
                  value={template.footer.left}
                  onChange={(e) => updateFooter("left", e.target.value)}
                >
                  {FOOTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                {template.footer.left === "custom" && (
                  <Input
                    value={template.footer.leftText}
                    onChange={(e) => updateFooter("leftText", e.target.value)}
                    placeholder="Left text"
                    className="mt-2"
                  />
                )}
              </Field>
              <Field label="Center">
                <Select
                  value={template.footer.center}
                  onChange={(e) => updateFooter("center", e.target.value)}
                >
                  {FOOTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                {template.footer.center === "custom" && (
                  <Input
                    value={template.footer.centerText}
                    onChange={(e) =>
                      updateFooter("centerText", e.target.value)
                    }
                    placeholder="Center text"
                    className="mt-2"
                  />
                )}
              </Field>
              <Field label="Right">
                <Select
                  value={template.footer.right}
                  onChange={(e) => updateFooter("right", e.target.value)}
                >
                  {FOOTER_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </Select>
                {template.footer.right === "custom" && (
                  <Input
                    value={template.footer.rightText}
                    onChange={(e) => updateFooter("rightText", e.target.value)}
                    placeholder="Right text"
                    className="mt-2"
                  />
                )}
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Footer Font Size">
                <RadioGroup
                  value={template.footer.fontSize}
                  options={[
                    { value: "small", label: "Small (9px)" },
                    { value: "normal", label: "Normal (10px)" },
                  ]}
                  onChange={(v) => updateFooter("fontSize", v)}
                />
              </Field>
              <Field label="Footer Text Color">
                <ColorInput
                  value={template.footer.textColor}
                  onChange={(v) => updateFooter("textColor", v)}
                />
              </Field>
            </div>
          </Section>

          <Section title="Typography & Colors">
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Primary Color"
                hint="Table headers, section headings, borders"
              >
                <ColorInput
                  value={template.colors.primary}
                  onChange={(v) => {
                    updateColors("primary", v);
                    updateColors("headerBg", v);
                  }}
                />
              </Field>
              <Field label="Table Header Background">
                <ColorInput
                  value={template.colors.headerBg}
                  onChange={(v) => updateColors("headerBg", v)}
                />
              </Field>
              <Field label="Table Header Text">
                <ColorInput
                  value={template.colors.headerText}
                  onChange={(v) => updateColors("headerText", v)}
                />
              </Field>
              <Field label="Alternating Row Color">
                <ColorInput
                  value={template.colors.altRow}
                  onChange={(v) => updateColors("altRow", v)}
                />
              </Field>
              <Field label="Font Family">
                <Select
                  value={template.typography.fontFamily}
                  onChange={(e) => updateTypo("fontFamily", e.target.value)}
                >
                  <option value="Helvetica">Helvetica / Inter</option>
                  <option value="Arial">Arial</option>
                  <option value="Calibri">Calibri</option>
                  <option value="Times New Roman">Times New Roman</option>
                </Select>
              </Field>
              <Field label="Font Size (body)">
                <Select
                  value={String(template.typography.fontSize)}
                  onChange={(e) =>
                    updateTypo("fontSize", Number(e.target.value))
                  }
                >
                  <option value="10">10px</option>
                  <option value="11">11px</option>
                  <option value="12">12px</option>
                </Select>
              </Field>
            </div>
          </Section>

          <Section title="Page Settings">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Page Size">
                <Select
                  value={template.page.size}
                  onChange={(e) =>
                    updatePage(
                      "size",
                      e.target.value as ReportTemplate["page"]["size"],
                    )
                  }
                >
                  <option value="A4">A4</option>
                  <option value="Letter">Letter</option>
                  <option value="A3">A3</option>
                </Select>
              </Field>
              <Field label="Default Orientation">
                <RadioGroup
                  value={template.page.orientation}
                  options={[
                    { value: "portrait", label: "Portrait" },
                    { value: "landscape", label: "Landscape" },
                  ]}
                  onChange={(v) => updatePage("orientation", v)}
                />
              </Field>
              <Field label="Margins">
                <RadioGroup
                  value={template.page.margins}
                  options={[
                    { value: "narrow", label: "Narrow (1cm)" },
                    { value: "normal", label: "Normal (2cm)" },
                    { value: "wide", label: "Wide (3cm)" },
                  ]}
                  onChange={(v) => updatePage("margins", v)}
                />
              </Field>
            </div>
          </Section>

          <div className="flex flex-wrap items-center gap-3 pt-3">
            <Button onClick={onSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
              ) : (
                <Save className="h-4 w-4" strokeWidth={1.5} />
              )}
              Save Template
            </Button>
            <Button variant="outline" onClick={onReset}>
              <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
              Reset to Defaults
            </Button>
            {saved && (
              <span className="text-xs text-green-700">Saved successfully</span>
            )}
          </div>
        </div>

        {/* Right — Preview */}
        <div className="sticky top-4 self-start">
          <ReportPreview template={template} />
        </div>
      </div>
    </div>
  );
}

function BrandingTab({
  value,
  onChange,
  onSave,
  saving,
  saved,
}: {
  value: BrandingSettings;
  onChange: (v: BrandingSettings) => void;
  onSave: () => void;
  saving: boolean;
  saved: boolean;
}) {
  function set<K extends keyof BrandingSettings>(k: K, v: BrandingSettings[K]) {
    onChange({ ...value, [k]: v });
  }
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Branding</h2>
        <p className="text-xs text-muted-foreground">
          App-wide brand assets and the primary theme color.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Platform Logo">
          <LogoUploader value={value.logo} onChange={(v) => set("logo", v)} />
        </Field>
        <Field label="Favicon URL">
          <Input
            value={value.favicon ?? ""}
            onChange={(e) => set("favicon", e.target.value || null)}
            placeholder="/favicon.ico"
          />
        </Field>
        <Field label="Primary Color">
          <ColorInput
            value={value.primaryColor}
            onChange={(v) => set("primaryColor", v)}
          />
        </Field>
      </div>
      <div className="flex items-center gap-3 pt-4">
        <Button onClick={onSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
          ) : (
            <Save className="h-4 w-4" strokeWidth={1.5} />
          )}
          Save Branding
        </Button>
        {saved && (
          <span className="text-xs text-green-700">Saved successfully</span>
        )}
      </div>
    </div>
  );
}

export default function MasterConfigPage() {
  const [tab, setTab] = useState<Tab>("general");

  const general = useConfig<GeneralSettings>(
    "general_settings",
    DEFAULT_GENERAL_SETTINGS,
  );
  const reportTpl = useConfig<ReportTemplate>(
    "report_template",
    DEFAULT_REPORT_TEMPLATE,
  );
  const branding = useConfig<BrandingSettings>("branding", DEFAULT_BRANDING);

  const [generalState, setGeneralState] = useState<GeneralSettings>(
    DEFAULT_GENERAL_SETTINGS,
  );
  const [templateState, setTemplateState] = useState<ReportTemplate>(
    DEFAULT_REPORT_TEMPLATE,
  );
  const [brandingState, setBrandingState] =
    useState<BrandingSettings>(DEFAULT_BRANDING);

  useEffect(() => {
    if (general.data) setGeneralState(general.data);
  }, [general.data]);
  useEffect(() => {
    if (reportTpl.data) setTemplateState(reportTpl.data);
  }, [reportTpl.data]);
  useEffect(() => {
    if (branding.data) setBrandingState(branding.data);
  }, [branding.data]);

  const saveGeneral = useSaveConfig("general_settings");
  const saveTemplate = useSaveConfig("report_template");
  const saveBranding = useSaveConfig("branding");

  const [savedFlag, setSavedFlag] = useState<Tab | null>(null);
  const [resetTemplateOpen, setResetTemplateOpen] = useState(false);
  function flash(t: Tab) {
    setSavedFlag(t);
    setTimeout(() => setSavedFlag(null), 2500);
  }

  async function handleSaveGeneral() {
    await saveGeneral.mutateAsync(generalState);
    flash("general");
  }
  async function handleSaveTemplate() {
    await saveTemplate.mutateAsync(templateState);
    flash("report");
  }
  async function handleSaveBranding() {
    await saveBranding.mutateAsync(brandingState);
    flash("branding");
  }
  function handleResetTemplate() {
    setResetTemplateOpen(true);
  }

  function confirmResetTemplate() {
    setTemplateState(DEFAULT_REPORT_TEMPLATE);
  }

  const loading = general.isLoading || reportTpl.isLoading || branding.isLoading;

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Master Config" },
      ]}
    >
      <div className="w-full space-y-5">
        <div className="flex items-center gap-3">
          <Settings
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-2xl font-semibold tracking-tight">
            Master Config
          </h1>
        </div>

        <div
          className="flex gap-6 rounded-xl border p-5"
          style={{ borderColor: BORDER, background: CARD_BG }}
        >
          {/* Left sidebar tabs */}
          <div className="w-[200px] shrink-0 space-y-1">
            {TABS.map((t) => {
              const active = tab === t.id;
              const Icon =
                t.id === "general"
                  ? Sliders
                  : t.id === "report"
                  ? Settings
                  : Palette;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                    active
                      ? "bg-[hsl(210,55%,92%)] font-medium text-[hsl(210,100%,28%)]"
                      : "text-muted-foreground hover:bg-[hsl(210,50%,96%)] hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={1.5} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Right content */}
          <div className="min-w-0 flex-1">
            {loading ? (
              <div
                className="flex items-center justify-center py-16 text-sm"
                style={{ color: MUTED }}
              >
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  strokeWidth={1.5}
                />
                Loading settings…
              </div>
            ) : tab === "general" ? (
              <GeneralSettingsTab
                value={generalState}
                onChange={setGeneralState}
                onSave={handleSaveGeneral}
                saving={saveGeneral.isPending}
                saved={savedFlag === "general"}
              />
            ) : tab === "report" ? (
              <ReportTemplateTab
                template={templateState}
                onChange={setTemplateState}
                onSave={handleSaveTemplate}
                onReset={handleResetTemplate}
                saving={saveTemplate.isPending}
                saved={savedFlag === "report"}
              />
            ) : (
              <BrandingTab
                value={brandingState}
                onChange={setBrandingState}
                onSave={handleSaveBranding}
                saving={saveBranding.isPending}
                saved={savedFlag === "branding"}
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={resetTemplateOpen}
        onOpenChange={setResetTemplateOpen}
        title="Reset template?"
        description="Are you sure you want to reset the report template to default values? Your unsaved changes will be discarded."
        confirmLabel="Reset"
        variant="default"
        onConfirm={confirmResetTemplate}
      />
    </AppLayout>
  );
}
